(function () {
  'use strict';

  // Keeps the cart attribute `_21e_delivery` in sync with the worst-case delivery
  // period computed by sections/x-21e-cart-delivery-attr.liquid. Sufio reads that
  // attribute for the invoice instead of reading product metafields live, so the
  // delivery line on an issued invoice can no longer change when a lead time is
  // edited later.
  //
  // Also stamps the same key as a LINE PROPERTY on every cart line, with that
  // line's own period rather than the cart-wide worst case. The Delivery Estimate
  // extension reads it on the Thank You and Order Status pages, so the date under
  // each item stays whatever it was at purchase. Without it that page recomputes
  // from the live metafields on every view, and editing a lead time silently moves
  // the promised date on orders already placed.
  //
  // Both are written by the same passes for the same reason: the value only has to
  // be right at the moment the order is created, so a cart left sitting for weeks
  // gets restamped on its way to checkout rather than carrying a stale number.

  var KEY = '_21e_delivery';

  // Property writes go through /cart/change.js, which replaces a line's whole
  // property set, so each pass re-reads the cart and rewrites one line at a time.
  // Writing a property also changes that line's key, hence one line per pass and a
  // fresh read in between. The cap is a runaway guard, not an expected count —
  // real carts finish in as many passes as they have unstamped lines.
  var MAX_LINE_PASSES = 12;

  // Our own cart writes are marked in the URL so the network watcher below can
  // tell them apart from an app's or the customer's. A flag would not do: the
  // watcher decides at request time, and a real cart change fired while we happen
  // to be mid-write would be swallowed as ours and never trigger a resync.
  // Shopify ignores unknown query params on the cart endpoints.
  var SELF_MARK = '_21e=1';
  var SECTION = 'x-21e-cart-delivery-attr';
  var EL_ID = 'x-21e-delivery-attr';
  var DEBOUNCE = 150;

  // Checkout is the only moment the value has to be right, so it is verified
  // there. Never block the customer longer than this, whatever happens.
  var CHECKOUT_TIMEOUT = 1500;
  var CHECKOUT_SELECTOR = '[name="checkout"], .cart__checkout-button, [data-checkout]';

  // Every route that can change the cart. Apps mutate the cart directly (Kaching
  // Bundles adds its lines without publishing the theme's cart-update event), so
  // watching the network is the only way to catch all of them.
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Opt-in tracing, so the passive sync can be watched instead of inferred from
  // whatever /cart.js happens to hold. Enable with:
  //   localStorage.setItem('21e-delivery-debug', '1')
  var DEBUG = false;
  try {
    DEBUG = window.localStorage.getItem('21e-delivery-debug') === '1';
  } catch (e) {}

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[21e-delivery]');
    console.log.apply(console, args);
  }

  // State lives here rather than on the DOM node. The node is only ever a
  // convenience: something on the page removes it after parse, and the sync must
  // not depend on it surviving.
  var state = { computed: '', current: '', lines: {} };
  var inFlight = false;
  var pending = false;
  var timer = null;
  var checkoutVerified = false;

  function readFrom(scope) {
    var el = scope.getElementById(EL_ID);
    if (!el) return false;
    state.computed = el.getAttribute('data-computed') || '';
    state.current = el.getAttribute('data-current') || '';

    // Rebuilt rather than merged, so a line removed from the cart stops being
    // stamped instead of lingering from an earlier read.
    var lines = {};
    var spans = el.querySelectorAll('.x-21e-delivery-line');
    for (var i = 0; i < spans.length; i++) {
      var vid = spans[i].getAttribute('data-vid') || '';
      var val = spans[i].getAttribute('data-val') || '';
      if (vid && val) lines[vid] = val;
    }
    state.lines = lines;
    return true;
  }

  function writeAttribute() {
    // Never send an empty value: Shopify deletes an attribute when it is set to an
    // empty string, and an empty computation just means "nothing to say yet"
    // (empty cart, or items with no shipping metafields at all).
    if (!state.computed) {
      log('attribute skipped, nothing computed (empty cart?) — leaving', state.current, 'in place');
      return Promise.resolve();
    }
    if (state.computed === state.current) {
      log('attribute skipped, already', state.current);
      return Promise.resolve();
    }

    var value = state.computed;
    log('writing attribute', state.current, '->', value);

    // attributes on /cart/update.js merge by key, so other apps' attributes
    // (Aimerce's aimPK and friends) are left untouched. A form submit would not be
    // safe here, because it only carries the fields present in that one form.
    var payload = { attributes: {} };
    payload.attributes[KEY] = value;

    return fetch(root + 'cart/update.js?' + SELF_MARK, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function () {
        state.current = value;
        log('attribute written ok:', value);
      })
      .catch(function () {
        log('attribute write failed, falling back to live metafields on the invoice');
        // A failed sync is not worth interrupting the customer for. Sufio falls
        // back to reading the metafields live, which is the current behaviour.
      });
  }

  // Rewrites one line's properties, preserving every other property on it.
  // /cart/change.js REPLACES the property set rather than merging into it, so a
  // naive write would drop whatever an app put there (bundle links, gift-card
  // recipient, engraving). Addressed by 1-based line index, not line key, because
  // the key is derived from the properties we are about to change.
  function stampLine(index, item, value) {
    var props = {};
    var existing = item.properties || {};
    for (var k in existing) {
      if (Object.prototype.hasOwnProperty.call(existing, k)) props[k] = existing[k];
    }
    props[KEY] = value;

    log('stamping line', index, '->', value);

    // The quantity MUST be sent. /cart/change.js with properties but no quantity
    // resets the line to quantity 1, so every multi-unit line (2x Ofen 3, the
    // volume ladder) silently dropped to a single unit the moment it was stamped.
    // Verified against the live cart 2026-09-15: properties alone took 3 -> 1,
    // properties plus quantity kept 3.
    return fetch(root + 'cart/change.js?' + SELF_MARK, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: index, quantity: item.quantity, properties: props })
    });
  }

  // One unstamped line per pass, re-reading the cart in between. Resolves once
  // every line already carries the value the section computed for it.
  function syncLines(pass) {
    pass = pass || 0;
    if (pass >= MAX_LINE_PASSES) {
      log('line sync gave up after', pass, 'passes');
      return Promise.resolve();
    }

    return fetch(root + 'cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        for (var i = 0; i < items.length; i++) {
          var item = items[i];
          var want = state.lines[String(item.variant_id)];
          // No computed value for this variant (an app-added line the section
          // never saw) — leave it alone rather than guess.
          if (!want) continue;
          var have = (item.properties && item.properties[KEY]) || '';
          if (have === want) continue;

          return stampLine(i + 1, item, want).then(function () {
            return syncLines(pass + 1);
          });
        }
        log('lines in sync after', pass, 'pass(es)');
        return null;
      })
      .catch(function () {
        // Same trade-off as the attribute: a line that misses its stamp simply
        // shows no delivery date on the order status page. Never worth blocking
        // the customer over.
        log('line sync failed');
      });
  }

  function sync() {
    // A change that lands mid-request must not be dropped, or the attribute ends
    // up one cart change behind. Remember it and re-run once the request settles.
    if (inFlight) {
      pending = true;
      log('sync deferred, request in flight');
      return Promise.resolve();
    }

    inFlight = true;

    return writeAttribute()
      .then(function () {
        return syncLines(0);
      })
      .catch(function () {})
      .then(function () {
        inFlight = false;
        if (pending) {
          pending = false;
          return refresh();
        }
      });
  }

  function refresh() {
    // Cache-bust: the URL is otherwise identical on every call and a cached body
    // would silently reintroduce a stale-by-one value.
    return fetch(root + '?section_id=' + SECTION + '&_=' + Date.now(), { cache: 'no-store' })
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!readFrom(doc)) {
          log('section fetched but the element was missing from it');
          return;
        }
        log('section says computed=' + state.computed, 'current=' + state.current);
        return sync();
      })
      .catch(function () {
        log('section fetch failed');
      });
  }

  function refreshDebounced() {
    // The cart moved, so a previous checkout verification no longer counts.
    checkoutVerified = false;
    clearTimeout(timer);
    timer = setTimeout(refresh, DEBOUNCE);
  }

  // ---------------------------------------------------------------- add merge
  //
  // Stamping changes a line's properties, and Shopify only merges an add into an
  // existing line when the properties match exactly. So without this, adding a
  // product that is already in the cart (4, then 4 more) opened a second line
  // instead of making it 8. Pricing was never wrong (the volume discounts count
  // across lines), but two lines of the same miner look broken.
  //
  // Fix: when an add targets a variant whose cart line carries nothing but our
  // stamp, the add is sent with that same stamp, and Shopify merges it into the
  // line by itself. Nothing is merged after the fact, so the add response the
  // drawer renders is already the merged line and no extra cart writes happen.
  //
  // Strictly opt-in per request. Anything unusual (other properties, selling
  // plans, several items for one variant, a body shape we do not parse, a failed
  // or slow cart read) sends the request exactly as the caller built it, which is
  // the old behaviour: a second line, never anything worse.

  var CART_ADD = /\/cart\/add(\.js)?(\?|$)/;
  var ADD_LOOKUP_TIMEOUT = 800;

  // The value to carry for a variant: the stamp of its existing line, but only if
  // that line holds our stamp and nothing else. Read from the cart itself, not from
  // the section's computed value, because the two can differ for a moment after a
  // lead time changes, and only an exact match merges.
  function stampsByVariant(cart) {
    var out = {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var vid = String(item.variant_id);
      var props = item.properties || {};
      var keys = Object.keys(props);
      var onlyOurs = keys.length === 1 && keys[0] === KEY && props[KEY];
      if (!onlyOurs || item.selling_plan_allocation) continue;
      if (!Object.prototype.hasOwnProperty.call(out, vid)) out[vid] = props[KEY];
    }
    return out;
  }

  // Returns the variant ids the body adds as plain items (no properties, no
  // selling plan), or null when the body is anything we do not fully understand.
  function plainAddedVariants(body) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      var id = body.get('id');
      if (!id || body.getAll('id').length !== 1) return null;
      var blocked = false;
      body.forEach(function (value, key) {
        if (key.indexOf('properties') === 0 || key.indexOf('items') === 0 || (key === 'selling_plan' && value)) {
          blocked = true;
        }
      });
      return blocked ? null : [String(id)];
    }

    if (typeof body === 'string') {
      var data;
      try {
        data = JSON.parse(body);
      } catch (e) {
        return null;
      }
      if (!data || typeof data !== 'object') return null;
      var items = Array.isArray(data.items) ? data.items : data.id ? [data] : null;
      if (!items || !items.length) return null;
      var ids = [];
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (!it || !it.id) return null;
        if (it.properties && Object.keys(it.properties).length) continue;
        if (it.selling_plan) continue;
        ids.push(String(it.id));
      }
      return ids;
    }

    return null;
  }

  // Same body with the stamp added to the matching plain items. Built fresh, the
  // caller's own body object is never mutated.
  function withStamps(body, stamps) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      var id = String(body.get('id'));
      if (!stamps[id]) return body;
      var copy = new FormData();
      body.forEach(function (value, key) {
        copy.append(key, value);
      });
      copy.append('properties[' + KEY + ']', stamps[id]);
      return copy;
    }

    var data = JSON.parse(body);
    var list = Array.isArray(data.items) ? data.items : [data];
    var changed = false;
    for (var i = 0; i < list.length; i++) {
      var it = list[i];
      var hasProps = it.properties && Object.keys(it.properties).length;
      var stamp = stamps[String(it.id)];
      if (!stamp || hasProps || it.selling_plan) continue;
      it.properties = {};
      it.properties[KEY] = stamp;
      changed = true;
    }
    return changed ? JSON.stringify(data) : body;
  }

  // Returns null when this add has nothing to merge into, so the caller sends the
  // request exactly as before, synchronously. Otherwise resolves to the init to
  // send: the caller's own, or a copy carrying stamps.
  function prepareAdd(init) {
    var ids = init && init.body != null ? plainAddedVariants(init.body) : null;
    if (!ids || !ids.length) return null;

    // Two plain items for the same variant in one request would both be stamped
    // and Shopify's merge order is not ours to predict. Leave those alone.
    var seen = {};
    for (var i = 0; i < ids.length; i++) {
      if (seen[ids[i]]) return null;
      seen[ids[i]] = true;
    }

    // Only pay for a cart read when the variant is known to be in the cart. A
    // first add, the common case, goes out with no delay at all.
    var inCart = false;
    for (var j = 0; j < ids.length; j++) {
      if (state.lines[ids[j]]) inCart = true;
    }
    if (!inCart) return null;

    var lookup = originalFetch(root + 'cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var stamps = stampsByVariant(cart);
        var body = withStamps(init.body, stamps);
        if (body === init.body) return init;
        log('add carries existing stamp so it merges into the line', stamps);
        var next = {};
        for (var k in init) {
          if (Object.prototype.hasOwnProperty.call(init, k)) next[k] = init[k];
        }
        next.body = body;
        return next;
      });

    var timeout = new Promise(function (resolve) {
      setTimeout(function () {
        resolve(init);
      }, ADD_LOOKUP_TIMEOUT);
    });

    return Promise.race([lookup, timeout]).catch(function () {
      return init;
    });
  }

  var originalFetch = null;

  // Watch cart mutations at the network level so app-driven changes are covered
  // too. Apart from the add merge above, the wrapper returns the original promise
  // untouched.
  function watchFetch() {
    if (!window.fetch || window.fetch.__x21eCartWatched) return;

    var original = window.fetch;
    originalFetch = function () {
      return original.apply(window, arguments);
    };
    var wrapped = function (input, init) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      } catch (e) {}

      var isOurOwn = url.indexOf(SELF_MARK) !== -1;
      var self = this;
      var result;

      var pending = null;
      if (typeof input === 'string' && CART_ADD.test(url) && !isOurOwn) {
        try {
          pending = prepareAdd(init);
        } catch (e) {
          pending = null;
        }
      }

      if (pending) {
        result = pending.then(function (prepared) {
          return original.call(self, input, prepared);
        });
      } else {
        result = original.apply(this, arguments);
      }

      if (url && CART_MUTATION.test(url) && !isOurOwn) {
        result.then(function () {
          log('cart mutated via fetch:', url);
          refreshDebounced();
        }, function () {});
      }

      return result;
    };

    wrapped.__x21eCartWatched = true;
    window.fetch = wrapped;
  }

  function watchXhr() {
    if (!window.XMLHttpRequest || XMLHttpRequest.prototype.__x21eCartWatched) return;

    var open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (typeof url === 'string' && CART_MUTATION.test(url)) {
        this.addEventListener('load', refreshDebounced);
      }
      return open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.__x21eCartWatched = true;
  }

  // Last line of defence. Event-driven syncing can still lose a race — a debounced
  // refresh is cancelled if the customer leaves the page before it fires — and the
  // value only has to be correct at the moment the order is created. So verify
  // once more on the way to checkout, holding the click just long enough.
  function guardCheckout(event) {
    if (checkoutVerified) return;

    var target = event.target;
    var trigger = target && target.closest ? target.closest(CHECKOUT_SELECTOR) : null;
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();
    log('checkout clicked, verifying before letting it through');

    var released = false;
    function release() {
      if (released) return;
      released = true;
      checkoutVerified = true;
      log('checkout released with', state.current);
      trigger.click();
    }

    setTimeout(release, CHECKOUT_TIMEOUT);
    refresh().then(release, release);
  }

  function init() {
    // The in-page element is server-rendered with the current cart, so it can be
    // trusted on load. If it is missing — it gets stripped from the DOM on some
    // pages — fall back to fetching the section instead of doing nothing.
    if (readFrom(document)) {
      log('init from in-page element:', state.computed, '/', state.current);
      sync();
    } else {
      log('init: in-page element missing, fetching the section instead');
      refresh();
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refreshDebounced);
      log('subscribed to cart-update');
    } else {
      log('WARNING: pubsub unavailable, relying on the network watcher alone');
    }

    document.addEventListener('click', guardCheckout, true);
  }

  // Wrap as early as possible, before app scripts start mutating the cart.
  watchFetch();
  watchXhr();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.CartDeliveryAttribute = {
    refresh: refresh,
    refreshDebounced: refreshDebounced,
    state: state
  };
})();
